// The inbox: notification rows as the bell lists them, how often to ask for the unread count, and where a
// row jumps to. Pure.
//
// Delivery: pushed, with a slow poll behind it. Every tab keeps a socket to its person's inbox room
// (inboxClient.ts); the server signals it after a commit that notified them, and the tab reads the
// unread count at once, which keeps an @mention under the 2 s acceptance goal wherever the two people
// are. While that socket is live the count is polled only every PUSHED_POLL_MS (60 s), in case a signal
// was lost. Without it (no realtime on the server, or the socket is down) the poll is what it was before
// push: every FOCUSED_POLL_MS (1.5 s) while the window is focused and visible, every 20 s while it is
// visible but not focused. A hidden window does not ask at all, and asks at once when it gets focus back.
// The call is one indexed count per user.
import { DEPARTMENT_LABEL, type Department, isDepartment } from "../departments.ts";
import type { Route } from "../route.ts";

export type NotificationKind =
  | "mention"
  | "reply"
  | "review_requested"
  | "review_decided"
  | "delivery"
  | "assignment"
  | "acceptance";

export interface Notification {
  id: string;
  kind: NotificationKind;
  actor_id: string | null;
  actor_name: string | null;
  project_id: string | null;
  subject_type: string;
  subject_id: string;
  target_type: string | null;
  target_id: string | null;
  data: Record<string, unknown>;
  created_at: string;
  read_at: string | null;
}

export interface UnreadCount {
  unread: number;
  by_kind: Record<string, number>;
}

export const FOCUSED_POLL_MS = 1500;
export const BACKGROUND_POLL_MS = 20_000;
export const PUSHED_POLL_MS = 60_000;

/**
 * How long until the next unread-count call; null when the page is hidden (focus brings it back).
 * `pushed`: the inbox socket is live, so signals bring changes and the poll is only a safety net.
 */
export function pollDelay(o: { visible: boolean; focused: boolean; pushed?: boolean }): number | null {
  if (!o.visible) return null;
  if (o.pushed) return PUSHED_POLL_MS;
  return o.focused ? FOCUSED_POLL_MS : BACKGROUND_POLL_MS;
}

const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

/** One line for a row: who did what. */
/** The department a sign-off notification is about (F4 department steps carry it in data). */
const deptOf = (n: Partial<Pick<Notification, "data">>): Department | null => {
  const d = str(n.data?.department);
  return d && isDepartment(d) ? d : null;
};

/** Who a notification is from: the member, or a guest of a review link ("Robin (guest)"). */
export function notificationActor(n: Pick<Notification, "actor_name" | "data">): string {
  if (n.actor_name) return n.actor_name;
  const guest = str(n.data?.guest_name);
  return guest ? `${guest} (guest)` : "Someone";
}

export function notificationTitle(n: Notification): string {
  const who = notificationActor(n);
  const status = str(n.data.status);
  const dept = deptOf(n);
  const what = dept ? DEPARTMENT_LABEL[dept].toLowerCase() : null;
  switch (n.kind) {
    case "mention":
      return `${who} mentioned you`;
    case "reply":
      return `${who} replied`;
    case "review_requested":
      return what ? `${who} asked you to sign off ${what}` : `${who} asked you to sign off`;
    case "review_decided":
      if (what)
        return str(n.data.decision) === "changes_requested"
          ? `${who} asked for changes on ${what}`
          : `${who} signed off ${what}`;
      return status === "approved"
        ? `${who} signed off`
        : status === "changes_requested"
          ? `${who} asked for changes`
          : `${who} decided a sign-off`;
    case "delivery":
      return `${who} delivered`;
    case "assignment":
      return `${who} assigned you`;
    case "acceptance":
      return `${who} accepted`;
  }
}

/** The quoted text under the title, when there is one. */
export const notificationExcerpt = (n: Notification): string | null =>
  str(n.data.excerpt) ?? str(n.data.note) ?? str(n.data.title);

export const KIND_LABEL: Record<NotificationKind, string> = {
  mention: "Mention",
  reply: "Reply",
  review_requested: "Sign-off",
  review_decided: "Sign-off",
  delivery: "Delivery",
  assignment: "Assignment",
  acceptance: "Accepted",
};

/** Thread a row is about (comment notifications carry it in data). */
export const notificationThread = (n: Notification): string | null => str(n.data.thread_id);

/**
 * Where a row goes. Take and version threads need the thread's shot, which the caller fetches and passes
 * in; a scene needs its episode. Null: the row has nowhere to go (it is still marked read).
 */
export function notificationRoute(
  n: Pick<Notification, "project_id" | "target_type" | "target_id"> & Partial<Pick<Notification, "data">>,
  extra: { shotId?: string | null; episodeOfScene?: string | null } = {},
): Route | null {
  const pid = n.project_id;
  const id = n.target_id;
  if (!pid) return null;
  if (["maker_brief", "maker_assignment", "maker_delivery"].includes(n.target_type ?? "")) {
    const assignmentId = str(n.data?.assignment_id) ?? (n.target_type === "maker_assignment" ? id : null);
    return {
      view: "project",
      projectId: pid,
      loc: {
        kind: "makers",
        ...(assignmentId ? { page: { t: "assignment", id: assignmentId } as const } : {}),
      },
    };
  }
  if (!id) return { view: "project", projectId: pid, loc: { kind: "root" } };
  switch (n.target_type) {
    case "shot":
      return { view: "shot", projectId: pid, shotId: id };
    case "take":
    case "asset_version":
      return extra.shotId
        ? { view: "shot", projectId: pid, shotId: extra.shotId }
        : { view: "project", projectId: pid, loc: { kind: "root" } };
    case "episode": {
      const dept = deptOf(n);
      return { view: "project", projectId: pid, loc: { kind: "episode", id, ...(dept ? { dept } : {}) } };
    }
    case "scene":
      return extra.episodeOfScene
        ? { view: "project", projectId: pid, loc: { kind: "scene", episodeId: extra.episodeOfScene, id } }
        : { view: "project", projectId: pid, loc: { kind: "root" } };
    case "folder":
      return { view: "project", projectId: pid, loc: { kind: "folder", id } };
    case "element":
      return { view: "element", projectId: pid, elementId: id };
    case "canvas":
    case "canvas_node":
      return { view: "canvas", projectId: pid, id };
    case "script_revision": {
      // a note on the script: its episode's Writing step
      const episodeId = str(n.data?.episode_id);
      return episodeId
        ? { view: "project", projectId: pid, loc: { kind: "episode", id: episodeId, dept: "writing" } }
        : { view: "project", projectId: pid, loc: { kind: "root" } };
    }
    default:
      return { view: "project", projectId: pid, loc: { kind: "root" } };
  }
}

/** Roll back the displayed rows on a refused write, even when the unread count has not changed. */
export async function markInboxRead(
  items: Notification[] | null,
  ids: string[] | "all",
  write: () => Promise<unknown>,
  publish: (items: Notification[] | null) => void,
) {
  publish(
    items?.map((n) =>
      ids === "all" || ids.includes(n.id) ? { ...n, read_at: n.read_at ?? new Date().toISOString() } : n,
    ) ?? null,
  );
  try {
    await write();
  } catch (e) {
    publish(items);
    throw e;
  }
}
